/**
 * @fileoverview The theme editor: values for named tokens, and a preview that
 * is the reader's own answer rather than a second opinion.
 *
 * THE PREVIEW RUNS THE READER'S PARSER ON EVERY KEYSTROKE, which is the whole
 * design of this screen. The draft is built into tags, parsed back by
 * `parseThemeEvent`, and what comes out is both what the preview wears and what
 * the field labels report. So a value that readers will drop is shown as
 * dropped WHILE IT IS BEING TYPED, and the publisher's refusal is a backstop
 * rather than the first time the author hears about it.
 *
 * That matters more here than it would elsewhere, because a theme fails
 * SILENTLY by nature: a rejected token is not an error at read time, it is the
 * default value, and the page renders perfectly. Without this the author's only
 * feedback would be a page that looks exactly like it did before.
 *
 * THE PREVIEW IS SCOPED THE WAY THE PAGE IS, to an element of its own, never to
 * `:root`. It is the same wall the reader enforces and it is enforced here for
 * a second reason: this screen shows our own chrome beside the preview, so a
 * preview that leaked would restyle the editor around it and an author would be
 * designing against a moving target.
 *
 * THERE IS NO VISIBILITY CONTROL AND THERE CANNOT BE ONE. A theme is fetched by
 * everybody who reads any page wearing it, so an encrypted theme would be a
 * theme nobody could render. The absence is structural.
 *
 * WHAT THIS SCREEN DOES NOT DO: point a page at the theme. That is a control on
 * the PAGE editor, because the reference lives in the page's own `theme` tag.
 * What this screen owes instead is the coordinate in a form that can be copied,
 * which is why it is shown after publishing rather than left to be assembled
 * out of three fields.
 */

import type { SignerInterface } from '@cloistr/auth';
import { useMemo, useState } from 'react';

import { useColourScheme } from '@/components/useColourScheme';
import { PAGE_THEME_KIND } from '@/services/pages/kinds';
import {
  publishTheme,
  type PublishedThemeAddress,
  type ThemePublishOutcome,
} from '@/services/pages/publishTheme';
import { isValidIdentifier, suggestedThemeIdentifier } from '@/services/pages/slug';
import {
  THEME_TOKEN_NAMES,
  buildThemeTags,
  grammarOf,
  parseThemeEvent,
  themeStyle,
  type ThemeDraft,
} from '@/services/pages/theme';

export type ThemeEditorDraft = ThemeDraft;

export function emptyThemeDraft(): ThemeEditorDraft {
  return {
    // Empty, not generated. `suggestedThemeIdentifier` says at length why a
    // theme's name is the author's to choose where a page's is not.
    identifier: suggestedThemeIdentifier(),
    title: '',
    description: '',
    tokens: {},
    carried: [],
  };
}

/**
 * What each grammar accepts, in the author's words.
 *
 * Written from the grammars rather than from the CSS specification: saying a
 * colour may be `rgb()` when this app's grammar takes only bare numbers inside
 * it would be a help text that produces rejected values.
 */
const GRAMMAR_HELP: Record<string, { placeholder: string; accepts: string }> = {
  colour: {
    placeholder: '#1c1917',
    accepts: 'A hex colour, a plain name like “teal”, or rgb()/hsl() with plain numbers.',
  },
  length: {
    placeholder: '1.25rem',
    accepts: 'A number with a unit — rem, px, ch, em, % — and never negative.',
  },
  number: { placeholder: '1.6', accepts: 'A plain number between 0.8 and 4.' },
  fontStack: {
    placeholder: 'Georgia, serif',
    accepts: 'Font names only, separated by commas. A theme cannot ship a font file.',
  },
};

const GROUPS: { heading: string; note: string; grammar: string }[] = [
  { heading: 'Colour', note: 'Text, borders and the accent.', grammar: 'colour' },
  {
    heading: 'Type',
    note: 'Families a reader already has. Naming a font nobody has changes nothing.',
    grammar: 'fontStack',
  },
  { heading: 'Spacing and size', note: 'Room, corners and the reading measure.', grammar: 'length' },
  { heading: 'Line height', note: '', grammar: 'number' },
];

export function ThemeEditor({
  signer,
  initial,
  existing,
  onPublished,
}: {
  signer: SignerInterface;
  initial: ThemeEditorDraft;
  existing: boolean;
  onPublished: (address: PublishedThemeAddress) => void;
}) {
  const [draft, setDraft] = useState<ThemeEditorDraft>(initial);
  const scheme = useColourScheme();
  const [publishing, setPublishing] = useState(false);
  const [outcome, setOutcome] = useState<ThemePublishOutcome | null>(null);
  const [publishedHere, setPublishedHere] = useState(false);

  const identifierOk = isValidIdentifier(draft.identifier.trim().toLowerCase());

  // THE READER'S OWN ANSWER, recomputed as the author types. See the file
  // comment: this is the preview AND the per-field verdict, from one parse, so
  // the two can never disagree about whether a value took effect.
  const asRead = useMemo(() => {
    const { tags, content } = buildThemeTags(draft);
    return parseThemeEvent(
      { kind: PAGE_THEME_KIND, pubkey: '0'.repeat(64), created_at: 0, content, tags },
      PAGE_THEME_KIND
    );
  }, [draft]);

  const rejected = useMemo(() => new Set(asRead?.rejected ?? []), [asRead]);
  const applied = asRead ? Object.keys(asRead.variables).length : 0;

  function setToken(name: string, value: string) {
    setDraft((d) => ({ ...d, tokens: { ...d.tokens, [name]: value } }));
  }

  const canPublish = !publishing && identifierOk && rejected.size === 0;

  async function onPublish() {
    setPublishing(true);
    setOutcome(null);
    // An edit may write over its own theme. So may a second save of a theme
    // made in this editor a moment ago, which is now an edit too.
    const result = await publishTheme(draft, signer, undefined, {
      replacing: existing || publishedHere,
    });
    setOutcome(result);
    setPublishing(false);
    if (result.status === 'published') {
      setPublishedHere(true);
      onPublished(result.address);
    }
  }

  return (
    <div className="editor">
      <div className="editor-form">
        <h1 className="editor-heading">{existing ? 'Edit theme' : 'Make a theme'}</h1>

        <p className="editor-note">
          A theme is values for named tokens, never CSS, and one theme can be worn by many
          pages — so editing it restyles all of them at once. It is public: everyone who reads
          a page wearing it fetches it.
        </p>

        <label className="editor-field">
          <span className="editor-label">Name</span>
          <input
            className="editor-input"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            placeholder="What is this theme called?"
          />
        </label>

        {/* The address. Editable exactly once in a theme's life. */}
        <div className="editor-field">
          <span className="editor-label">Address</span>
          {existing ? (
            <>
              <p className="editor-fixed">{draft.identifier}</p>
              <p className="editor-note">
                A published theme&rsquo;s address cannot be changed. Changing it would not
                rename this theme, it would leave this one where it is and start a second one
                — and every page wearing this one would keep wearing this one.
              </p>
            </>
          ) : (
            <>
              <input
                className="editor-input"
                value={draft.identifier}
                onChange={(e) => setDraft((d) => ({ ...d, identifier: e.target.value }))}
                placeholder="lowercase-words-with-hyphens"
                aria-invalid={draft.identifier !== '' && !identifierOk}
              />
              <p className="editor-note">
                {draft.identifier !== '' && !identifierOk
                  ? 'Lowercase letters, numbers and hyphens only.'
                  : 'This name is permanent and public. You will type it into the pages that wear this theme, so make it one you will recognise.'}
              </p>
            </>
          )}
        </div>

        <label className="editor-field">
          <span className="editor-label">Description</span>
          <input
            className="editor-input"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="One line, so you can find it again"
          />
        </label>

        {GROUPS.map((group) => (
          <fieldset className="theme-group" key={group.grammar}>
            <legend className="theme-group-heading">{group.heading}</legend>
            {group.note && <p className="editor-note">{group.note}</p>}
            {THEME_TOKEN_NAMES.filter((name) => grammarOf(name) === group.grammar).map((name) => (
              <TokenField
                key={name}
                name={name}
                grammar={group.grammar}
                value={draft.tokens[name] ?? ''}
                dropped={rejected.has(name)}
                onChange={(value) => setToken(name, value)}
              />
            ))}
          </fieldset>
        ))}

        {applied === 0 && (
          <p className="editor-warning">
            This theme sets nothing, so pages wearing it render in the default styling. That is
            a real thing to publish — it is how you return every page wearing this theme to
            defaults in one edit — but it is worth being sure you meant it.
          </p>
        )}

        <button className="editor-publish" onClick={() => void onPublish()} disabled={!canPublish}>
          {publishing ? 'Publishing…' : existing ? 'Publish the changes' : 'Publish'}
        </button>

        {rejected.size > 0 && (
          <p className="editor-warning">
            {rejected.size === 1 ? 'One value is' : `${rejected.size} values are`} not something
            a reader can use, so {rejected.size === 1 ? 'it' : 'they'} would do nothing. Fix{' '}
            {rejected.size === 1 ? 'it' : 'them'} above and this will publish.
          </p>
        )}

        {outcome && <ThemePublishResult outcome={outcome} />}
      </div>

      {/* THE PREVIEW WEARS THE THEME AND NOTHING ELSE DOES. `style` puts the
          tokens on this element, so they reach its own subtree and nothing
          above it -- the same wall the reading route enforces, and here it also
          stops the preview restyling the editor around it. */}
      <div className="editor-preview">
        <span className="editor-preview-label">Preview</span>
        <div className="page" style={themeStyle(asRead, scheme)} data-themed={applied > 0 || undefined}>
          <header className="page-header">
            <h1 className="page-title">{draft.title || 'A page wearing this theme'}</h1>
            <p className="page-summary">
              The summary sits under the title, in the muted colour.
            </p>
          </header>
          <div className="page-body">
            <h2>A heading</h2>
            <p>
              Ordinary prose, at the measure and line height this theme sets. Long enough to
              wrap, because a measure that is never reached is a measure you cannot see.
            </p>
            <blockquote>A quotation, which borrows the border colour.</blockquote>
            <pre>
              <code>a monospace line, in the mono stack</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function TokenField({
  name,
  grammar,
  value,
  dropped,
  onChange,
}: {
  name: string;
  grammar: string;
  value: string;
  dropped: boolean;
  onChange: (value: string) => void;
}) {
  const help = GRAMMAR_HELP[grammar];
  return (
    // THE VERDICT SITS OUTSIDE THE LABEL, tied to the input by
    // `aria-describedby` instead. Inside it, the field's accessible name would
    // become the token name PLUS the complaint the moment a value was rejected
    // -- so a screen reader would announce the error as part of the field's
    // name, and anything looking the field up by name would stop finding it
    // exactly when something had gone wrong with it.
    <div className="theme-token">
      <label className="theme-token-label">
        <span className="theme-token-name">{name}</span>
        <input
          className="editor-input"
          // The token's own name on the element. A browser proof has to address
          // ONE field exactly, and matching on the visible name cannot: `text`
          // is a prefix of `text-muted` and `text-dim`, so a substring match
          // would silently drive whichever came first.
          data-token={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={help?.placeholder}
          aria-invalid={dropped}
          aria-describedby={dropped ? `${name}-dropped` : undefined}
        />
      </label>
      {dropped && (
        <span className="theme-token-dropped" id={`${name}-dropped`}>
          A reader would ignore this. {help?.accepts}
        </span>
      )}
    </div>
  );
}

/**
 * What happened, in the author's terms.
 *
 * SEPARATE FROM THE PAGE'S `PublishResult` rather than shared with it, because
 * the outcomes are different shapes and the one that matters here -- a value
 * the reader would drop -- has no page equivalent at all. Forcing one component
 * to speak for both would mean a union of two unions and a screen full of
 * branches that can never fire.
 */
function ThemePublishResult({ outcome }: { outcome: ThemePublishOutcome }) {
  if (outcome.status === 'published') {
    return (
      <div className="editor-result">
        <p>Published. Pages wear this theme by carrying its address:</p>
        <p className="editor-fixed">{outcome.coordinate}</p>
        <p className="editor-note">
          Editing this theme changes every page carrying that address. Accepted by{' '}
          {outcome.acceptedBy.join(', ')}.
        </p>
      </div>
    );
  }

  if (outcome.status === 'invalid-tokens') {
    return (
      <div className="editor-result editor-result-bad">
        <p>
          Not published. {outcome.rejected.join(', ')} would be ignored by readers, so the
          theme would have looked saved and done nothing.
        </p>
      </div>
    );
  }

  if (outcome.status === 'invalid-name') {
    return (
      <div className="editor-result editor-result-bad">
        <p>Not published. “{outcome.identifier}” cannot be part of an address.</p>
      </div>
    );
  }

  if (outcome.status === 'too-large') {
    return (
      <div className="editor-result editor-result-bad">
        <p>
          Not published. This theme is {outcome.bytes.toLocaleString()} bytes and {outcome.relay}{' '}
          accepts {outcome.limit.toLocaleString()}
          {outcome.advertised ? '' : ' (its own limit is not advertised, so that is our default)'}.
        </p>
      </div>
    );
  }

  if (outcome.status === 'name-taken') {
    return (
      <div className="editor-result editor-result-bad">
        <p>
          Not published. You already have a theme at “{outcome.identifier}”, and publishing this
          one there would restyle every page that wears it. Choose a different address, or open
          that theme to edit it.
        </p>
      </div>
    );
  }

  if (outcome.status === 'name-unchecked') {
    return (
      <div className="editor-result editor-result-bad">
        <p>
          Not published. These relays did not answer, so there was no way to know whether you
          already have a theme at “{outcome.identifier}”:
        </p>
        <ul className="editor-refusals">
          {outcome.relays.map((relay) => (
            <li key={relay}>{relay}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (outcome.status === 'signer-failed') {
    return (
      <div className="editor-result editor-result-bad">
        <p>Not published. The signer said: {outcome.message}</p>
      </div>
    );
  }

  return (
    <div className="editor-result editor-result-bad">
      <p>Not published. Every relay refused it:</p>
      <ul className="editor-refusals">
        {Object.entries(outcome.reasons).map(([relay, reason]) => (
          <li key={relay}>
            {relay}: {reason}
          </li>
        ))}
      </ul>
    </div>
  );
}
